import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import KSTADIUM, {
  KStadiumJWTPayload,
  KSTADIUM_ACCESS_TOKEN_KEY,
  KSTADIUM_REFRESH_TOKEN_KEY,
} from '../../services/kstadium.service';
import { RootState } from '../store';
import { decodePayloadFromJWT, validateJWT } from '../../utils/jwt';

export type UserData = KStadiumJWTPayload['info'];

// 현재 지원되는 토큰 목록
export const SUPPORTED_TOKENS = [`KSTA`, `SOP`];

export const STAY_SIGNED_IN_STORAGE_KEY = `staySignIn`;

export interface JwtTokens {
  accessToken: string;
  refreshToken: string;
}

// 액션 Payload 타입
export type LoginPayload = {
  userId: string;
  password: string;
};

// 초기 상태 타입
export type KstadiumState = {
  accessToken: string | null;
  refreshToken: string | null;
  userData: UserData | null;
  isLoadingUserData: boolean;
};

// 초기 상태
const initialState: KstadiumState = {
  accessToken: null,
  refreshToken: null,
  userData: null,
  isLoadingUserData: false,
};

// async thunk
export const refreshJwtToken = createAsyncThunk<
  void,
  void,
  { state: RootState }
>(`kstadium/refreshJwtToken`, async (param, { dispatch }) => {
  const refreshToken = KSTADIUM.getRefreshToken();

  if (!refreshToken) {
    throw new Error(`Refresh token not exists`);
  }

  try {
    const { data } = await KSTADIUM.postRefreshToken();

    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    dispatch(setJwtTokens(data));
  } catch (e) {
    // 실패하면 로그아웃
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    dispatch(logout());
    throw e;
  }
});

// 리듀서 슬라이스
const kstadiumSlice = createSlice({
  name: `kstadium`,
  initialState,
  reducers: {
    // JWT 토큰 업데이트 (localStorage와 userData를 함께 업데이트)
    setJwtTokens(state: KstadiumState, action: PayloadAction<JwtTokens>) {
      const { accessToken, refreshToken } = action.payload;
      try {
        // JWT 토큰 유효성 확인 (실패시 에러 던짐)
        validateJWT(accessToken, true);

        const payload = decodePayloadFromJWT<KStadiumJWTPayload>(accessToken);
        localStorage.setItem(KSTADIUM_ACCESS_TOKEN_KEY, accessToken);
        localStorage.setItem(KSTADIUM_REFRESH_TOKEN_KEY, refreshToken);
        state.accessToken = accessToken;
        state.refreshToken = refreshToken;
        state.userData = payload.info;
      } catch (e) {
        console.error(e);
      }
    },
    logout(state: KstadiumState) {
      localStorage.removeItem(KSTADIUM_ACCESS_TOKEN_KEY);
      localStorage.removeItem(KSTADIUM_REFRESH_TOKEN_KEY);
      state.accessToken = null;
      state.refreshToken = null;
      state.userData = null;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(refreshJwtToken.pending, (state: KstadiumState) => {
      state.isLoadingUserData = true;
    });
    builder.addCase(refreshJwtToken.rejected, (state: KstadiumState) => {
      state.isLoadingUserData = false;
    });
    builder.addCase(refreshJwtToken.fulfilled, (state: KstadiumState) => {
      state.isLoadingUserData = false;
    });
  },
});

// 리듀서 & 액션 리턴
export const { setJwtTokens, logout } = kstadiumSlice.actions;
export const selectKstadiumSlice = (state: RootState): KstadiumState =>
  state.kstadium;
export default kstadiumSlice.reducer;
